/**
 * Account Settings Page
 *
 * Displays user profile information, password change form, and capability list.
 * Available to all authenticated users.
 *
 * Sections:
 * 1. Profile Info — read-only card with name, email, tenant, role, last login
 * 2. Change Password — form with current/new/confirm password fields
 * 3. Capabilities — grouped badge list of effective capabilities
 */

import {
  User,
  Mail,
  Building2,
  Shield,
  Clock,
  Key,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Lock,
} from "lucide-react";
import type { ReactElement, FormEvent } from "react";
import { useState, useCallback } from "react";

import { apiRequest } from "../../api/client";
import { useAuthStore } from "../../store/authStore";

// ============================================================================
// Types
// ============================================================================

interface PasswordFormState {
  readonly currentPassword: string;
  readonly newPassword: string;
  readonly confirmPassword: string;
}

interface PasswordFormErrors {
  readonly currentPassword?: string;
  readonly newPassword?: string;
  readonly confirmPassword?: string;
}

type PasswordChangeStatus = "idle" | "submitting" | "success" | "error";

interface CapabilityGroup {
  readonly prefix: string;
  readonly label: string;
  readonly capabilities: readonly string[];
}

// ============================================================================
// Constants
// ============================================================================

const INITIAL_FORM_STATE: PasswordFormState = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const CAPABILITY_GROUP_LABELS: Readonly<Record<string, string>> = {
  config: "Configuration",
  products: "Products",
  analytics: "Analytics",
  admin: "Administration",
};

// ============================================================================
// Main Component
// ============================================================================

export function AccountSettingsPage(): ReactElement {
  const { user, tenantId, tenantSlug, capabilities } = useAuthStore();

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Account Settings</h1>
        <p className="mt-1 text-foreground-muted">
          Manage your account profile and security settings
        </p>
      </div>

      <div className="space-y-6">
        {/* Profile Info Section */}
        <ProfileInfoCard user={user} tenantId={tenantId} tenantSlug={tenantSlug} />

        {/* Change Password Section */}
        <ChangePasswordCard />

        {/* Capabilities Section */}
        <CapabilitiesCard capabilities={capabilities} />
      </div>
    </div>
  );
}

// ============================================================================
// Profile Info Card
// ============================================================================

interface ProfileInfoCardProps {
  readonly user: { name: string; email: string; avatarUrl?: string } | undefined;
  readonly tenantId: string | undefined;
  readonly tenantSlug: string | undefined;
}

function ProfileInfoCard({ user, tenantId, tenantSlug }: ProfileInfoCardProps): ReactElement {
  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <User className="h-5 w-5" />
        Profile Information
      </h2>
      <dl className="grid gap-4 sm:grid-cols-2">
        <div className="flex items-start gap-3">
          <User className="h-5 w-5 mt-0.5 text-foreground-muted" />
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Name</dt>
            <dd className="mt-0.5" data-testid="profile-name">
              {user?.name ?? "Unknown"}
            </dd>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Mail className="h-5 w-5 mt-0.5 text-foreground-muted" />
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Email</dt>
            <dd className="mt-0.5" data-testid="profile-email">
              {user?.email ?? "Unknown"}
            </dd>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Building2 className="h-5 w-5 mt-0.5 text-foreground-muted" />
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Tenant</dt>
            <dd className="mt-0.5" data-testid="profile-tenant">
              {tenantSlug ?? tenantId ?? "No tenant"}
            </dd>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Clock className="h-5 w-5 mt-0.5 text-foreground-muted" />
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Session</dt>
            <dd className="mt-0.5 text-sm text-foreground-muted">Active</dd>
          </div>
        </div>
      </dl>
    </div>
  );
}

// ============================================================================
// Change Password Card
// ============================================================================

function ChangePasswordCard(): ReactElement {
  const [form, setForm] = useState<PasswordFormState>(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<PasswordFormErrors>({});
  const [status, setStatus] = useState<PasswordChangeStatus>("idle");
  const [apiError, setApiError] = useState<string>("");

  const validateForm = useCallback((formData: PasswordFormState): PasswordFormErrors => {
    const result: Record<string, string> = {};

    if (!formData.currentPassword) {
      result["currentPassword"] = "Current password is required";
    }

    if (!formData.newPassword) {
      result["newPassword"] = "New password is required";
    } else if (formData.newPassword.length < 8) {
      result["newPassword"] = "Password must be at least 8 characters";
    } else if (formData.currentPassword && formData.newPassword === formData.currentPassword) {
      result["newPassword"] = "New password must be different from current password";
    }

    if (!formData.confirmPassword) {
      result["confirmPassword"] = "Please confirm your new password";
    } else if (formData.newPassword && formData.confirmPassword !== formData.newPassword) {
      result["confirmPassword"] = "Passwords do not match";
    }

    return result as PasswordFormErrors;
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();

      const validationErrors = validateForm(form);
      setErrors(validationErrors);

      const hasErrors =
        validationErrors.currentPassword !== undefined ||
        validationErrors.newPassword !== undefined ||
        validationErrors.confirmPassword !== undefined;

      if (hasErrors) {
        return;
      }

      setStatus("submitting");
      setApiError("");

      try {
        const response = await apiRequest<{ success: boolean }>("/api/v1/auth/password", {
          method: "PATCH",
          body: {
            currentPassword: form.currentPassword,
            newPassword: form.newPassword,
          },
        });

        if (response.success) {
          setStatus("success");
          setForm(INITIAL_FORM_STATE);
          setErrors({});
        } else {
          setStatus("error");
          setApiError(response.error.message);
        }
      } catch {
        setStatus("error");
        setApiError("An unexpected error occurred. Please try again.");
      }
    },
    [form, validateForm]
  );

  const handleFieldChange = useCallback(
    (field: keyof PasswordFormState, value: string): void => {
      setForm((prev) => ({ ...prev, [field]: value }));
      // Clear field error on change — rebuild without the cleared field
      if (errors[field]) {
        setErrors((prev) => {
          const next: Record<string, string> = {};
          for (const [k, v] of Object.entries(prev)) {
            if (k !== field && v !== undefined) next[k] = v;
          }
          return next as PasswordFormErrors;
        });
      }
      // Clear success/error status when user starts typing again
      if (status === "success" || status === "error") {
        setStatus("idle");
        setApiError("");
      }
    },
    [errors, status]
  );

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Key className="h-5 w-5" />
        Change Password
      </h2>

      {status === "success" && (
        <div
          className="mb-4 flex items-center gap-2 rounded-md border border-success bg-success/10 p-3"
          role="alert"
        >
          <CheckCircle2 className="h-4 w-4 text-success" />
          <p className="text-sm text-success">Password changed successfully</p>
        </div>
      )}

      {status === "error" && apiError && (
        <div
          className="mb-4 flex items-center gap-2 rounded-md border border-error bg-error/10 p-3"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-error" />
          <p className="text-sm text-error">{apiError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-medium mb-1">
            Current Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
            <input
              id="currentPassword"
              type="password"
              value={form.currentPassword}
              onChange={(e) => handleFieldChange("currentPassword", e.target.value)}
              className={`w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                errors.currentPassword
                  ? "border-error focus:border-error focus:ring-error"
                  : "border-border focus:border-primary focus:ring-primary"
              }`}
              disabled={status === "submitting"}
              autoComplete="current-password"
            />
          </div>
          {errors.currentPassword && (
            <p className="mt-1 text-xs text-error">{errors.currentPassword}</p>
          )}
        </div>

        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium mb-1">
            New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
            <input
              id="newPassword"
              type="password"
              value={form.newPassword}
              onChange={(e) => handleFieldChange("newPassword", e.target.value)}
              className={`w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                errors.newPassword
                  ? "border-error focus:border-error focus:ring-error"
                  : "border-border focus:border-primary focus:ring-primary"
              }`}
              disabled={status === "submitting"}
              autoComplete="new-password"
            />
          </div>
          {errors.newPassword && <p className="mt-1 text-xs text-error">{errors.newPassword}</p>}
          <p className="mt-1 text-xs text-foreground-muted">Minimum 8 characters</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
            Confirm New Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
            <input
              id="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={(e) => handleFieldChange("confirmPassword", e.target.value)}
              className={`w-full rounded-md border bg-background pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                errors.confirmPassword
                  ? "border-error focus:border-error focus:ring-error"
                  : "border-border focus:border-primary focus:ring-primary"
              }`}
              disabled={status === "submitting"}
              autoComplete="new-password"
            />
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-error">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="pt-2">
          <button type="submit" disabled={status === "submitting"} className="btn btn-primary">
            {status === "submitting" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Changing Password...
              </>
            ) : (
              <>
                <Key className="mr-2 h-4 w-4" />
                Change Password
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// Capabilities Card
// ============================================================================

interface CapabilitiesCardProps {
  readonly capabilities: readonly string[];
}

function CapabilitiesCard({ capabilities }: CapabilitiesCardProps): ReactElement {
  const groups = groupCapabilities(capabilities);

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Shield className="h-5 w-5" />
        Capabilities
      </h2>
      <p className="text-sm text-foreground-muted mb-4">
        Your effective permissions based on assigned roles
      </p>

      {groups.length === 0 ? (
        <p className="text-sm text-foreground-muted">No capabilities assigned</p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.prefix}>
              <h3 className="text-sm font-medium text-foreground-muted mb-2">{group.label}</h3>
              <div
                className="flex flex-wrap gap-2"
                data-testid={`capability-group-${group.prefix}`}
              >
                {group.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="inline-flex items-center rounded-full border border-border bg-background-secondary px-2.5 py-0.5 text-xs font-medium"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Group capabilities by their prefix (e.g., "config:", "admin:").
 */
function groupCapabilities(capabilities: readonly string[]): readonly CapabilityGroup[] {
  const groups = new Map<string, string[]>();

  for (const cap of capabilities) {
    const colonIndex = cap.indexOf(":");
    const prefix = colonIndex >= 0 ? cap.substring(0, colonIndex) : "other";
    const existing = groups.get(prefix);
    if (existing) {
      existing.push(cap);
    } else {
      groups.set(prefix, [cap]);
    }
  }

  return Array.from(groups.entries()).map(([prefix, caps]) => ({
    prefix,
    label: CAPABILITY_GROUP_LABELS[prefix] ?? prefix.charAt(0).toUpperCase() + prefix.slice(1),
    capabilities: caps,
  }));
}
